import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const BASE = "https://binarex.co";

/**
 * Backwards-compatible SEO component.
 * - Existing usages: `<Seo title=... description=... path=... image=... />` keep working.
 * - On mount, also fetches /api/seo/meta?path=<currentPath> and merges admin overrides
 *   (admin override wins over caller-provided defaults, both win over the built-in fallback).
 * - Children (JsonLd snippets) are passed through to <Helmet>.
 */
export default function Seo({
  title,
  description,
  path,
  image,
  type = "website",
  noindex,
  keywords,
  children,
}) {
  const loc = useLocation();
  const resolvedPath = path || loc?.pathname || "/";
  const [api, setApi] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!BACKEND) return;
    axios
      .get(`${BACKEND}/api/seo/meta`, { params: { path: resolvedPath } })
      .then((r) => { if (!cancelled) setApi(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [resolvedPath]);

  // Resolution: API override (if admin set one) > prop > built-in default
  const fallbackTitle = title ? `${title} | BINAREX.CO` : "BINAREX.CO — All-in-One AI Tools, One Smart Platform";
  const fallbackDesc = description || "BINAREX.CO is an all-in-one AI tools platform by BINAREX AND CO. Access 47 AI tools — content writer, image generator, SEO, code, chat — with one account and one universal credit balance.";

  const finalTitle = api?.title || fallbackTitle;
  const finalDesc = api?.description || fallbackDesc;
  const finalKeywords = api?.keywords || keywords;
  const finalCanonical = api?.canonical || `${BASE}${resolvedPath}`;
  const finalOg = api?.og_image || image || `${BASE}/og-image.png`;
  const finalNoindex = typeof api?.noindex === "boolean" ? api.noindex : !!noindex;

  return (
    <Helmet>
      <title>{finalTitle}</title>
      <meta name="description" content={finalDesc} />
      {finalKeywords ? <meta name="keywords" content={finalKeywords} /> : null}
      <link rel="canonical" href={finalCanonical} />
      <meta name="robots" content={finalNoindex ? "noindex,nofollow" : "index,follow"} />

      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDesc} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={finalCanonical} />
      <meta property="og:image" content={finalOg} />
      <meta property="og:site_name" content="BINAREX AND CO" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDesc} />
      <meta name="twitter:image" content={finalOg} />

      {children}
    </Helmet>
  );
}

/* ---------------- JSON-LD helpers ----------------
 * Each helper renders its own <Helmet> so it gets merged into <head> at
 * runtime. They are NOT meant to be nested inside Seo — pass them as
 * SIBLINGS in the page tree. The Seo `children` prop is kept for back-compat
 * with native <meta>/<link>/<script> tags only.
 */

export function JsonLd({ data }) {
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(data)}</script>
    </Helmet>
  );
}

export function OrganizationJsonLd() {
  return (
    <JsonLd
      data={{
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "BINAREX AND CO",
        url: BASE,
        logo: `${BASE}/og-image.png`,
        sameAs: [
          "https://twitter.com/binarex",
          "https://www.linkedin.com/company/binarex",
        ],
        contactPoint: [
          {
            "@type": "ContactPoint",
            email: "support@binarex.co",
            contactType: "customer support",
            areaServed: "Worldwide",
          },
        ],
      }}
    />
  );
}

export function ProductJsonLd({ name, description, image, price, currency = "USD" }) {
  return (
    <JsonLd
      data={{
        "@context": "https://schema.org",
        "@type": "Product",
        name,
        description,
        image,
        brand: { "@type": "Brand", name: "BINAREX AND CO" },
        offers: price
          ? {
              "@type": "Offer",
              priceCurrency: currency,
              price: String(price),
              availability: "https://schema.org/InStock",
            }
          : undefined,
      }}
    />
  );
}

export function BlogPostingJsonLd({ title, description, image, datePublished, author, url }) {
  return (
    <JsonLd
      data={{
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        headline: title,
        description,
        image,
        datePublished,
        author: { "@type": "Person", name: author || "BINAREX Team" },
        publisher: {
          "@type": "Organization",
          name: "BINAREX AND CO",
          logo: { "@type": "ImageObject", url: `${BASE}/og-image.png` },
        },
        mainEntityOfPage: { "@type": "WebPage", "@id": url },
      }}
    />
  );
}

export function FaqJsonLd({ items }) {
  if (!items || items.length === 0) return null;
  return (
    <JsonLd
      data={{
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: items.map((i) => ({
          "@type": "Question",
          name: i.q || i.question,
          acceptedAnswer: { "@type": "Answer", text: i.a || i.answer },
        })),
      }}
    />
  );
}
